/**
 * Optional composite quality score for admin sort (Phase 4).
 * Not seller-history weighted in v1.
 */

import {
  LISTING_HARD_GATE_KEYS,
  type ListingHardGatesResult,
} from "@/lib/listing-lifecycle/listingHardGates";

/** 0–100 integer: share of hard gates that passed. */
export function computeListingQualityScore(
  result: ListingHardGatesResult,
): number {
  const passed = LISTING_HARD_GATE_KEYS.filter(
    (key) => result.gates[key].pass,
  ).length;
  return Math.round((passed / LISTING_HARD_GATE_KEYS.length) * 100);
}

/**
 * Persistable snapshot: gate breakdown + quality score.
 * Safe to store in `items.listing_gate_breakdown` / `listing_quality_score`.
 */
export function buildListingGatePersistFields(result: ListingHardGatesResult): {
  listing_gate_breakdown: ListingHardGatesResult;
  listing_quality_score: number;
} {
  return {
    listing_gate_breakdown: result,
    listing_quality_score: computeListingQualityScore(result),
  };
}

/** PostHog / ops telemetry for auto-approve skip breakdown. */
export function buildListingHardGateTelemetryProperties(
  result: ListingHardGatesResult,
): {
  listing_quality_score: number;
  hard_gates_pass: boolean;
  failed_hard_gates: (typeof LISTING_HARD_GATE_KEYS)[number][];
  hard_gate_breakdown: Record<
    (typeof LISTING_HARD_GATE_KEYS)[number],
    { pass: boolean; detail: string | null }
  >;
} {
  const failed_hard_gates = LISTING_HARD_GATE_KEYS.filter(
    (key) => !result.gates[key].pass,
  );
  const hard_gate_breakdown = Object.fromEntries(
    LISTING_HARD_GATE_KEYS.map((key) => [
      key,
      {
        pass: result.gates[key].pass,
        detail: result.gates[key].detail,
      },
    ]),
  ) as Record<
    (typeof LISTING_HARD_GATE_KEYS)[number],
    { pass: boolean; detail: string | null }
  >;

  return {
    listing_quality_score: computeListingQualityScore(result),
    hard_gates_pass: result.pass,
    failed_hard_gates,
    hard_gate_breakdown,
  };
}
